import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { MessageSquareText, ShieldCheck } from "lucide-react";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui";
import Logo from "@/components/layout/Logo";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { savePostAuthPath } from "@/lib/auth-client";

export function Login() {
  const auth = useAuth();
  const { t } = useT();
  const location = useLocation();
  const [starting, setStarting] = useState(false);
  const requestedPath =
    (location.state as { from?: string } | null)?.from ?? "/";

  useEffect(() => {
    if (auth.status === "authenticated") {
      // Handled by Navigate below — keep effect for SSR-safe paths.
    }
  }, [auth.status]);

  if (auth.status === "authenticated") {
    return <Navigate to={requestedPath} replace />;
  }

  const handleSignIn = async () => {
    setStarting(true);
    try {
      savePostAuthPath(requestedPath);
      await auth.signIn();
    } catch (e) {
      console.error(e);
      setStarting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-white to-brand-50">
      <div className="absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full bg-brand-200/40 blur-3xl" aria-hidden />
      <div className="absolute -left-20 bottom-0 h-[320px] w-[320px] rounded-full bg-brand-100/60 blur-3xl" aria-hidden />

      <header className="relative z-10 flex items-center justify-between px-6 pt-6 lg:px-10">
        <Logo />
        <LanguageSwitcher compact />
      </header>

      <main className="relative z-10 mx-auto flex max-w-md flex-col px-6 pb-16 pt-16 sm:pt-24">
        <div className="inline-flex items-center gap-2 self-start rounded-full border border-brand-100 bg-white/80 px-3 py-1 text-xs font-medium text-brand-700 backdrop-blur">
          <ShieldCheck className="h-3.5 w-3.5" />
          {t("auth.secure_signin")}
        </div>

        <h1 className="mt-6 text-4xl font-semibold tracking-tight text-slate-900 sm:text-[44px] sm:leading-tight">
          {t("auth.login_title")}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-slate-600">
          {t("auth.login_subtitle")}
        </p>

        <ul className="mt-8 space-y-3 text-sm text-slate-700">
          <li className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-xl bg-brand-50 text-brand-700">
              <MessageSquareText className="h-4 w-4" />
            </span>
            {t("auth.feature_whatsapp")}
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-xl bg-brand-50 text-brand-700">
              <ShieldCheck className="h-4 w-4" />
            </span>
            {t("auth.feature_no_password")}
          </li>
        </ul>

        <Button
          size="lg"
          block
          loading={starting || auth.status === "loading"}
          onClick={handleSignIn}
          className="mt-10"
        >
          {t("auth.signin_cta")}
        </Button>

        <p className="mt-4 text-center text-xs text-slate-500">
          {t("auth.legal_prefix")}{" "}
          <a href="https://pahcertificar.com/legal/privacidad" className="underline decoration-brand-300 underline-offset-4 hover:decoration-brand-600">
            {t("auth.legal_privacy")}
          </a>{" "}
          {t("auth.legal_and")}{" "}
          <a href="https://pahcertificar.com/legal/terminos" className="underline decoration-brand-300 underline-offset-4 hover:decoration-brand-600">
            {t("auth.legal_terms")}
          </a>
          .
        </p>
      </main>
    </div>
  );
}
